/*
 * Modules dependencies
 */

import { Badge } from 'src/components/core/badges/badge';
import { Button } from 'src/components/core/buttons/button';
import { CardGradient } from './card-gradient';
import { Coverage, Coverages } from 'src/components/coverages';
import { ReactNode, useState } from 'react';
import { Text } from 'src/components/core/text';
import { media } from '@untile/react-core/styles/media';
import { useTranslation } from 'next-i18next';
import ArrowRight from 'src/assets/svgs/arrow-right.svg';
import ChevronDown from 'src/assets/svgs/chevron-down.svg';
import Close from 'src/assets/svgs/close.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  coverages?: Coverage;
  description?: string;
  detailsLabel?: string;
  detailsUrl?: string;
  hideDetailsUrl?: boolean;
  hidePackHeader?: boolean;
  hidePackUrls?: boolean;
  label?: string;
  pricringCards: ReactNode;
  title?: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled(CardGradient)`
  padding: var(--space-xxs) 0;
`;

/**
 * `ContentWrapper` styled component.
 */

const ContentWrapper = styled.div`
  grid-area: content;
  padding: var(--space-xxs) var(--space-xs);

  ${media.min.md`
    padding: var(--space-sm) var(--space-xs) var(--space-xxs);
  `}

  ${media.min.xl`
    padding: var(--space-sm) var(--space-sm) var(--space-xxs);
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text).attrs({ as: 'h3', variant: 'labelL', weight: 600 })`
  background: var(--gradient-primary--15-deg);
  -webkit-background-clip: text;
  background-clip: text;
  color: var(--color-primary);
  padding: var(--space-xxs) 0;
  -webkit-text-fill-color: transparent;
  text-transform: uppercase;
`;

/**
 * `PricingGrid` styled component.
 */

const PricingGrid = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: pricing;
  padding: 0 var(--space-xxs);
  row-gap: var(--space-xxs);

  &[data-is-coverages-open='true'] {
    div {
      height: auto;
    }
  }
`;

/**
 * `CoveragesWrapper` styled component.
 */

const CoveragesWrapper = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  overflow: hidden;
  transition: grid-template-rows var(--transition-default);

  &[aria-expanded='true'] {
    grid-template-rows: 1fr;
  }
`;

/**
 * `ButtonsWrapper` styled component.
 */

const ButtonsWrapper = styled.div`
  column-gap: var(--space-sm);
  display: flex;
  justify-content: end;
  padding: var(--space-xs) var(--space-sm) var(--space-xxs) var(--space-sm);

  ${media.min.md`
    padding: var(--space-xxs) var(--space-sm) 0; 
  `}

  &[data-align-start='true'] {
    ${media.min.md`
      justify-content: start;
      padding: var(--space-xxs) var(--space-xs) 0; 
    `}

    ${media.min.xl`
      padding: var(--space-xxs) var(--space-sm) 0; 
    `}
  }

  &[data-justify-between='true'] {
    justify-content: space-between;
  }
`;

/**
 * Export `CardPricingPack` component.
 */

export function CardPricingPack(props: Props) {
  const {
    coverages,
    description,
    detailsLabel,
    detailsUrl,
    hideDetailsUrl,
    hidePackHeader,
    hidePackUrls,
    label,
    pricringCards,
    title
  } = props;

  const { t } = useTranslation('common');
  const [isCoveragesOpen, setIsCoveragesOpen] = useState(false);

  return (
    <Wrapper>
      {(title || description) && !hidePackHeader && (
        <ContentWrapper>
          <div style={{ alignItems: 'center', display: 'flex' }}>
            {title && <Title>{title}</Title>}

            {label && <Badge style={{ marginLeft: 'var(--space-xxs)' }}>{label}</Badge>}
          </div>

          {description && (
            <Text as={'p'} style={{ padding: 'var(--space-xxs) 0' }}>
              {description}
            </Text>
          )}
        </ContentWrapper>
      )}

      <PricingGrid data-is-coverages-open={isCoveragesOpen}>{pricringCards}</PricingGrid>

      {(detailsUrl || coverages) && !hidePackUrls && (
        <div style={{ gridArea: 'footer' }}>
          {coverages && (
            <CoveragesWrapper aria-expanded={isCoveragesOpen}>
              <div style={{ minHeight: 0, overflow: 'hidden' }}>{coverages && <Coverages {...coverages} />}</div>
            </CoveragesWrapper>
          )}

          <ButtonsWrapper
            data-align-start={!!coverages && !hidePackHeader}
            data-justify-between={!!coverages && !!detailsUrl}
          >
            {coverages && (
              <Button
                aria-label={t(`common:actions.${isCoveragesOpen ? 'close' : 'open'}Coverages`)}
                colorTheme={'text'}
                icon={isCoveragesOpen ? Close : ChevronDown}
                onClick={() => setIsCoveragesOpen(!isCoveragesOpen)}
                reverse
              >
                {t(`common:actions.${isCoveragesOpen ? 'close' : 'open'}Coverages`)}
              </Button>
            )}

            {detailsUrl && !hideDetailsUrl && (
              <Button
                aria-label={detailsLabel ? detailsLabel : t('common:actions.details')}
                colorTheme={'text'}
                href={detailsUrl}
                icon={ArrowRight}
                reverse
              >
                {detailsLabel ? detailsLabel : t('common:actions.details')}
              </Button>
            )}
          </ButtonsWrapper>
        </div>
      )}
    </Wrapper>
  );
}
